import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, MessageCircle } from 'lucide-react';

const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'HOME', path: '/' },
    { name: 'ABOUT US', path: '/about' },
    { name: 'PRODUCTS', path: '/products' },
    { name: 'INDUSTRIES', path: '/industries' },
    { name: 'QUALITY & SUPPLY', path: '/quality' },
    { name: 'CONTACT', path: '/contact' }
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white sticky top-0 z-50 border-b shadow-sm">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_voltron-calcium/artifacts/r3ufd964_ChatGPT%20Image%20Feb%2026%2C%202026%20at%2012_49_15%20PM.png"
                alt="Voltron Calcium Logo" 
                className="h-14 w-auto"
                onError={(e) => { e.target.src = '/voltron-logo.png'; }}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 text-sm font-semibold transition-colors ${
                    isActive(item.path)
                      ? 'text-blue-600'
                      : 'text-gray-700 hover:text-blue-600'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <Link to="/contact">
                <Button className="ml-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold">
                  GET QUOTE
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t pt-4">
              {navigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-4 py-3 text-sm font-semibold ${
                    isActive(item.path)
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="block mt-4"
              >
                <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold">
                  GET QUOTE
                </Button>
              </Link>
            </div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-grow">{children}</main>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/918679475554"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-lg transition-all hover:scale-110 z-40"
        aria-label="Contact us on WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </a>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Company Info */}
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_voltron-calcium/artifacts/r3ufd964_ChatGPT%20Image%20Feb%2026%2C%202026%20at%2012_49_15%20PM.png"
                alt="Voltron Calcium Logo"
                className="h-16 mb-4"
              />
              <h1 className="text-xl font-bold mb-4">
                <span className="text-white">VOLTRON</span>{' '}
                <span className="text-blue-400">CALCIUM</span>
              </h1>
              <p className="text-gray-400 mb-4">
                Your trusted partner for industrial chemicals and minerals supply across India.
              </p>
              <p className="text-sm text-gray-400">
                <strong>GSTIN:</strong> 05AAHCV3224B2ZT
              </p>
            </div>

            {/* Registered Office */}
            <div>
              <h4 className="font-bold text-lg mb-4">Registered Office</h4>
              <p className="text-gray-400 text-sm leading-relaxed">
                402/C2, Third Floor<br />
                Omega Heights<br />
                Dehradun, Uttarakhand – 248001<br />
                India
              </p>
            </div>

            {/* Corporate Office */}
            <div>
              <h4 className="font-bold text-lg mb-4">Corporate Office</h4>
              <p className="text-gray-400 text-sm leading-relaxed">
                F-14, Industrial Estate<br />
                Delhi Road<br />
                Saharanpur, Uttar Pradesh – 247001<br />
                India
              </p>
              <p className="mt-4 text-sm">
                <strong>Phone:</strong>{' '}
                <a href="tel:+918679475554" className="text-blue-400 hover:text-blue-300">
                  +91-86794 75554
                </a>
              </p>
              <p className="mt-2 text-sm">
                <strong>Email:</strong>{' '}
                <a href="mailto:sales@voltroncalcium.com" className="text-blue-400 hover:text-blue-300">
                  sales@voltroncalcium.com
                </a>
              </p>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>© 2025 Voltron Calcium Pvt Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
